import { useState } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Eye, Heart, Users, FileText, Target, Calendar } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ImageWithFallback } from './figma/ImageWithFallback';

// Post timeline data
const postTimelineData = [
  { date: 'Jan 1', posts: 12 },
  { date: 'Jan 8', posts: 18 },
  { date: 'Jan 15', posts: 24 },
  { date: 'Jan 22', posts: 32 },
  { date: 'Jan 29', posts: 28 },
  { date: 'Feb 5', posts: 22 },
  { date: 'Feb 12', posts: 16 },
  { date: 'Feb 19', posts: 12 },
  { date: 'Feb 26', posts: 8 }
];

// Posts over time with multiple metrics
const performanceOverTimeData = [
  { week: 'Week 1', posts: 12, emv: 24000, impressions: 145000, engagements: 8200 },
  { week: 'Week 2', posts: 18, emv: 36000, impressions: 198000, engagements: 12400 },
  { week: 'Week 3', posts: 24, emv: 48000, impressions: 256000, engagements: 16800 },
  { week: 'Week 4', posts: 32, emv: 52000, impressions: 289000, engagements: 18200 },
  { week: 'Week 5', posts: 28, emv: 45000, impressions: 234000, engagements: 14500 },
  { week: 'Week 6', posts: 22, emv: 38000, impressions: 189000, engagements: 11200 },
  { week: 'Week 7', posts: 16, emv: 28000, impressions: 156000, engagements: 9800 },
  { week: 'Week 8', posts: 18, emv: 32000, impressions: 178000, engagements: 11600 },
  { week: 'Week 9', posts: 28, emv: 46000, impressions: 245000, engagements: 15800 },
  { week: 'Week 10', posts: 24, emv: 42000, impressions: 221000, engagements: 14200 },
  { week: 'Week 11', posts: 20, emv: 36000, impressions: 198000, engagements: 12800 },
  { week: 'Week 12', posts: 22, emv: 38000, impressions: 205000, engagements: 13400 }
];

// Post distribution by platform
const postDistributionData = [
  { name: 'Instagram Feed', value: 45, color: '#e4405f' },
  { name: 'Instagram Stories', value: 28, color: '#c13584' },
  { name: 'TikTok', value: 18, color: '#000000' },
  { name: 'YouTube', value: 9, color: '#ff0000' }
];

// Performance details
const performanceDetails = [
  {
    id: 1,
    ambassador: 'Sarah Johnson',
    avatar: 'https://i.pravatar.cc/150?img=1',
    posts: 24,
    impressions: '458K',
    engagements: '32.4K',
    emv: '$48,200',
    roi: '3.2x',
    trend: 'up'
  },
  {
    id: 2,
    ambassador: 'Mike Chen',
    avatar: 'https://i.pravatar.cc/150?img=2',
    posts: 18,
    impressions: '312K',
    engagements: '24.8K',
    emv: '$38,900',
    roi: '2.8x',
    trend: 'up'
  },
  {
    id: 3,
    ambassador: 'Emma Wilson',
    avatar: 'https://i.pravatar.cc/150?img=3',
    posts: 21,
    impressions: '389K',
    engagements: '28.2K',
    emv: '$42,100',
    roi: '3.0x',
    trend: 'up'
  },
  {
    id: 4,
    ambassador: 'David Park',
    avatar: 'https://i.pravatar.cc/150?img=4',
    posts: 16,
    impressions: '267K',
    engagements: '19.6K',
    emv: '$32,400',
    roi: '2.5x',
    trend: 'down'
  }
];

export function AmbassadorHubStatistics() {
  const [selectedMetric, setSelectedMetric] = useState<'posts' | 'emv' | 'impressions' | 'engagements'>('posts');
  const [timeFilter, _setTimeFilter] = useState('Week');

  const metricMapping = {
    posts: { key: 'posts', label: 'Posts', color: '#f59e0b' },
    emv: { key: 'emv', label: 'EMV ($)', color: '#8b5cf6', formatter: (val: number) => `$${(val / 1000).toFixed(0)}K` },
    impressions: { key: 'impressions', label: 'Impressions', color: '#3b82f6', formatter: (val: number) => `${(val / 1000).toFixed(0)}K` },
    engagements: { key: 'engagements', label: 'Engagements', color: '#10b981', formatter: (val: number) => `${(val / 1000).toFixed(1)}K` }
  };

  const currentMetric = metricMapping[selectedMetric];

  return (
    <div className="space-y-6">
      {/* KPI Metrics - 2 rows of 4 cards */}
      <div className="grid grid-cols-4 gap-4">
        {/* Row 1 */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="text-sm text-gray-600">Active Influencers</div>
              <div className="w-10 h-10 rounded-lg bg-[#f3ecff] flex items-center justify-center">
                <Users className="w-5 h-5 text-[#be43ff]" />
              </div>
            </div>
            <div className="text-3xl text-gray-900 mb-1">259</div>
            <div className="flex items-center gap-1 text-sm text-green-600">
              <TrendingUp className="w-4 h-4" />
              <span>+12 this month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="text-sm text-gray-600">Posts</div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <div className="text-3xl text-gray-900 mb-1">2,150</div>
            <div className="flex items-center gap-1 text-sm text-green-600">
              <TrendingUp className="w-4 h-4" />
              <span>+260 vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="text-sm text-gray-600">Impressions</div>
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
            <div className="text-3xl text-gray-900 mb-1">2.8M</div>
            <div className="flex items-center gap-1 text-sm text-green-600">
              <TrendingUp className="w-4 h-4" />
              <span>+340K</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="text-sm text-gray-600">Engagements</div>
              <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
                <Heart className="w-5 h-5 text-pink-600" />
              </div>
            </div>
            <div className="text-3xl text-gray-900 mb-1">184K</div>
            <div className="flex items-center gap-1 text-sm text-green-600">
              <TrendingUp className="w-4 h-4" />
              <span>+28K</span>
            </div>
          </CardContent>
        </Card>

        {/* Row 2 */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="text-sm text-gray-600">Total Reach</div>
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-cyan-600" />
              </div>
            </div>
            <div className="text-3xl text-gray-900 mb-1">4.2M</div>
            <div className="flex items-center gap-1 text-sm text-green-600">
              <TrendingUp className="w-4 h-4" />
              <span>+520K</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="text-sm text-gray-600">EMV</div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <div className="text-3xl text-gray-900 mb-1">$486K</div>
            <div className="flex items-center gap-1 text-sm text-green-600">
              <TrendingUp className="w-4 h-4" />
              <span>+62K</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="text-sm text-gray-600">Spend</div>
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            <div className="text-3xl text-gray-900 mb-1">$124K</div>
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <span>Within budget</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="text-sm text-gray-600">ROI</div>
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <div className="text-3xl text-gray-900 mb-1">3.9x</div>
            <div className="flex items-center gap-1 text-sm text-green-600">
              <TrendingUp className="w-4 h-4" />
              <span>+0.4x</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Over Time Chart */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedMetric('posts')}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  selectedMetric === 'posts'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Posts
              </button>
              <button
                onClick={() => setSelectedMetric('emv')}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  selectedMetric === 'emv'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                EMV
              </button>
              <button
                onClick={() => setSelectedMetric('impressions')}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  selectedMetric === 'impressions'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Impressions
              </button>
              <button
                onClick={() => setSelectedMetric('engagements')}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  selectedMetric === 'engagements'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Engagements
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button className="px-4 py-2 rounded-lg text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50">
                <Calendar className="w-4 h-4 inline-block mr-2" />
                {timeFilter}
              </button>
            </div>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performanceOverTimeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="week"
                  stroke="#9ca3af"
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  stroke="#9ca3af"
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={'formatter' in currentMetric ? currentMetric.formatter : undefined}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  formatter={'formatter' in currentMetric ? currentMetric.formatter : undefined}
                />
                <Bar
                  dataKey={currentMetric.key}
                  fill={currentMetric.color}
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Post Timeline and Distribution Row */}
      <div className="grid grid-cols-2 gap-6">
        {/* Post Timeline */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-gray-900 mb-6">Post Timeline</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={postTimelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="posts" fill="#1e3a5f" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Post Distribution */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-gray-900 mb-6">Post Distribution</h3>
            <div className="flex items-center gap-8">
              <div className="w-48 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={postDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {postDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-3">
                {postDistributionData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="text-sm text-gray-700">{item.name}</span>
                    </div>
                    <span className="text-sm text-gray-900 font-medium">{item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Details Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-gray-900 mb-6">Performance Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">Ambassador</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">Posts</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">Impressions</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">Engagements</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">EMV</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">ROI</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">Trend</th>
                </tr>
              </thead>
              <tbody>
                {performanceDetails.map((detail) => (
                  <tr key={detail.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                          <ImageWithFallback
                            src={detail.avatar}
                            alt={detail.ambassador}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-sm text-gray-900 font-medium">{detail.ambassador}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-900">{detail.posts}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-900">{detail.impressions}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-900">{detail.engagements}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-900">{detail.emv}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-900 font-medium">{detail.roi}</span>
                    </td>
                    <td className="py-4 px-4">
                      {detail.trend === 'up' ? (
                        <TrendingUp className="w-4 h-4 text-green-600" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-600" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
